import { PropertyType, type CollectionSubPropertyType } from '@/backend/types'
import type {
  UpdateCollectionPropertyConfig,
  UpdateSelectPropertyConfig,
} from '@/backend/updateProperty'
import type {
  Property,
  PropertyInput,
  PropertyInputFilter,
} from '@/modules/Project/Properties/types'
import { useProjectStore } from '@/modules/Project/useProjectStore'
import { getRandomColor } from '@/modules/Project/utils'
import { deepStripUndefined } from '@/shared/utils'
import { convertPropertyType } from '@/shared/utils/property'
import { assertIsNotNullOrUndefined, invariant, nonNullable } from '@/shared/utils/typeAssertions'
import { isCollectionProperty, isSelectProperty } from '@/shared/utils/typeGuards'
import { ANALYTICS_EVENT, useAnalytics } from '@/sharedComposables/useAnalytics'
import { dequal } from 'dequal'
import { defineStore } from 'pinia'
import { computed, ref, toRaw, watch, type ComputedRef, type Ref } from 'vue'

const newOptionId = () => window.crypto.randomUUID()

/**
 * A pinia store backend for editing an agent property.
 *
 * We use a store to enable the app to visually react to changes in the property,
 * for example, changes to input ids highlight table columns, etc.
 */
export const usePropertyStore = defineStore('property', () => {
  const { captureAnalyticsEvent } = useAnalytics()

  const projectStore = useProjectStore()

  const selectedPropertyId = ref<string | null>(null)

  const originalProperty: ComputedRef<Property | null> = computed(() =>
    selectedPropertyId.value ? projectStore.propertiesById[selectedPropertyId.value] || null : null,
  )

  const editedProperty: Ref<Property | null> = ref(null)

  watch(originalProperty, (newOriginal, oldOriginal) => {
    const noChange = dequal(newOriginal, oldOriginal)
    if (noChange) {
      /**
       * Don't sync the property if the new property is the same as the old property,
       * as this will clear any in-progress edits. This can happen when we handle the
       * `'property:updated'` websocket message that arrives shortly after the user has
       * created a property.
       */
      return
    }

    if (newOriginal) {
      startEditingProperty(newOriginal.id)
    } else {
      stopEditingProperty()
    }
  })

  /**
   * An update request for properties requires us to explicitly specify
   *
   * - which properties got upserted (including information on rename)
   * - which properties got removed
   *
   * We track this here.
   * `editedProperty.config.options` is only used to populate this and is otherwise ignored.
   */
  const optionEdits = ref<
    {
      id: string
      from:
        | {
            value: string
            color: string | null
            tool_fallback: boolean
          }
        | undefined
      to: {
        value: string
        color: string | null
        tool_fallback: boolean
      }
      type: 'upsert' | 'remove'
    }[]
  >([])

  /**
   * Used when updating a select property's options. We need to send
   * this to the backend so that field values can be remapped.
   */
  const upsertCollectionProperties: Ref<
    UpdateCollectionPropertyConfig['upsertCollectionProperties']
  > = ref([])
  const removeCollectionProperties: Ref<
    UpdateCollectionPropertyConfig['removeCollectionProperties']
  > = ref([])

  /**
   * The edited input ids of the property. Set to original on prop selection.
   */
  const editedInputs = computed(() => editedProperty.value?.inputs ?? [])
  const includesInputId = (propertyId: string, entityId: string) =>
    editedInputs.value.some((input) => {
      if (input.type === 'directField') {
        return input.propertyId === propertyId && (!entityId || input.entityId === entityId)
      } else if (input.type === 'related' || input.type === 'viaReference') {
        return input.propertyId === propertyId && !entityId
      }
      return false
    })

  const editedConfig = computed<Extract<Property, { config: unknown }>['config'] | null>(() =>
    editedProperty.value && 'config' in editedProperty.value ? editedProperty.value.config : null,
  )

  const startEditingProperty = (propertyId: string): void => {
    stopEditingProperty()
    const property = projectStore.propertiesById[propertyId]
    if (!property) return
    selectedPropertyId.value = property.id
    editedProperty.value = structuredClone(toRaw(originalProperty.value))

    if (isSelectProperty(editedProperty.value)) {
      editedProperty.value.config.options.forEach((o) => {
        optionEdits.value.push({
          id: property.isOptimistic ? newOptionId() : o.value,
          from: property.isOptimistic
            ? undefined
            : {
                value: o.value,
                color: o.color ?? getRandomColor(),
                tool_fallback: o.tool_fallback,
              },
          to: {
            value: o.value,
            color: o.color ?? getRandomColor(),
            tool_fallback: o.tool_fallback,
          },
          type: 'upsert',
        })
      })
    }
  }

  const stopEditingProperty = (): void => {
    optionEdits.value = []
    upsertCollectionProperties.value = []
    removeCollectionProperties.value = []
    selectedPropertyId.value = null
    editedProperty.value = null
  }

  /**
   * Signals if any changes have been made to the property
   */
  const isDirty = computed(() => {
    const hasEditedOptions = optionEdits.value.some(
      (o) =>
        // new insert
        (!o.from && o.type !== 'remove') ||
        // existing delete
        (!!o.from && o.type === 'remove') ||
        // existing update
        (!!o.from && !dequal(o.from, o.to)),
    )
    const hasUpsertedCollectionProperties =
      upsertCollectionProperties.value && upsertCollectionProperties.value.length > 0

    const hasRemovedCollectionProperties =
      removeCollectionProperties.value && removeCollectionProperties.value.length > 0

    if (hasEditedOptions || hasUpsertedCollectionProperties || hasRemovedCollectionProperties) {
      /**
       * There is an edge case where the property might not have changed but
       * we still want to send an update. This is the case where the user
       * shifts select values around, e.g.
       * 1. "Option A" -> "Option B"
       * 2. "Option B" -> "Option A"
       * In this case, the options have stayed the same, but we still need to
       * send an update to the backend so that field values are remapped.
       */
      return true
    }

    const propertyHasChanged = !dequal(
      deepStripUndefined(originalProperty.value ?? {}),
      deepStripUndefined(editedProperty.value ?? {}),
    )
    return propertyHasChanged
  })

  const hasUnsavedOptions = computed(() => {
    if (!isSelectProperty(editedProperty.value) || !originalProperty.value) {
      return false
    }

    invariant(isSelectProperty(originalProperty.value))

    return dequal(originalProperty.value.config.options, editedProperty.value?.config.options)
  })

  /**
   * Selects or deselects an input id for the property
   */
  const toggleInputId = ({
    propertyId,
    entityId,
    viaPropertyId,
  }: {
    propertyId: string
    entityId?: string | null
    viaPropertyId?: string | null
  }) => {
    const index = editedInputs.value.findIndex((input) => {
      if (entityId && input.type === 'directField') {
        return input.propertyId === propertyId && input.entityId === entityId
      } else if (viaPropertyId && input.type === 'viaReference') {
        return input.propertyId === propertyId && input.viaPropertyId === viaPropertyId
      } else if (!entityId && !viaPropertyId && input.type === 'related') {
        return input.propertyId === propertyId
      }
      return false
    })
    if (index > -1) {
      editedInputs.value.splice(index, 1)
      captureAnalyticsEvent(ANALYTICS_EVENT.INPUT_SELECTED, { propertyId, entityId, value: false })
    } else {
      // Create the appropriate input type based on parameters
      let newInput: PropertyInput
      if (entityId) {
        newInput = {
          type: 'directField',
          propertyId,
          entityId,
        }
      } else if (viaPropertyId) {
        newInput = {
          type: 'viaReference',
          propertyId,
          viaPropertyId,
        }
      } else {
        newInput = {
          type: 'related',
          propertyId,
        }
      }
      editedInputs.value.push(newInput)
      captureAnalyticsEvent(ANALYTICS_EVENT.INPUT_SELECTED, { propertyId, entityId, value: true })
    }
  }

  /**
   * Replaces an input if it exists, otherwise adds it.
   */
  const setInput = ({
    propertyId,
    entityId,
    filters,
  }: {
    propertyId: string
    entityId?: string | null
    filters?: PropertyInputFilter | null
  }) => {
    if (!editedProperty.value) {
      return
    }
    const index = editedInputs.value.findIndex((input) => {
      if (entityId && input.type === 'directField') {
        return input.propertyId === propertyId && input.entityId === entityId
      } else if (!entityId && input.type === 'related') {
        return input.propertyId === propertyId
      }
      return false
    })

    // Create the appropriate input type based on parameters
    let newInput: PropertyInput
    if (entityId) {
      newInput = {
        type: 'directField',
        propertyId,
        entityId,
      }
    } else {
      newInput = {
        type: 'related',
        propertyId,
        filters: filters || undefined,
      }
    }

    if (index > -1) {
      editedProperty.value.inputs = editedProperty.value.inputs.with(index, newInput)
    } else {
      editedInputs.value.push(newInput)
    }
  }

  /**
   * Toggles usage of a Hub input for the property
   */
  const toggleHubInput = (hubId: string) => {
    if (!editedProperty.value) return

    const index = editedInputs.value.findIndex(
      (input) => input.type === 'directHub' && input.directHubId === hubId,
    )

    if (index !== -1) {
      editedInputs.value.splice(index, 1)
    } else {
      editedInputs.value.push({ type: 'directHub', directHubId: hubId })
    }
  }

  const updateSelectOption = (id: string, params: { value: string; color?: string | null }) => {
    const existing = optionEdits.value.find((o) => o.id === id)

    invariant(existing && existing.type === 'upsert')

    existing.to.value = params.value
    if (params.color || params.color === null) {
      existing.to.color = params.color
    }
  }

  const deleteSelectOption = (id: string) => {
    const existing = optionEdits.value.find((o) => o.id === id)
    if (!existing || existing.type === 'remove') return

    existing.type = 'remove'
  }

  const createSelectOption = (params: { value: string; color: string }) => {
    // reinserting a remove
    const existing = optionEdits.value.find((o) => o.to.value === params.value)

    if (existing?.type === 'remove') {
      existing.type = 'upsert'
      existing.to.color = params.color
      return
    }

    // do not create duplicates if we matched with an upsert
    if (existing) return

    // inserting new option (matching existing that isn't a remove should be impossible)
    optionEdits.value.push({
      id: newOptionId(),
      from: undefined,
      to: { value: params.value, color: params.color, tool_fallback: false },
      type: 'upsert',
    })
  }

  const toggleSelectOption = (params: { value: string; color: string }) => {
    const existing = optionEdits.value.find((o) => o.to.value === params.value)

    if (existing?.type === 'remove') {
      existing.type = 'upsert'
      existing.to.value = params.value
      existing.to.color = params.color
    } else if (existing) {
      deleteSelectOption(existing.id)
    } else {
      createSelectOption(params)
    }
  }

  /**
   * Marks an option as the default option - new entities will be assigned this option.
   */
  const markOptionAsDefault = (id: string | null) => {
    if (isSelectProperty(editedProperty.value)) {
      editedProperty.value.config.defaultOption = id
    }
  }

  /**
   * Mark options as fallback, so that the tool will fall back to them if it is
   * unable to otherwise choose a match.
   */
  const markOptionsAsFallback = (fallbackOptionIds: string[]) => {
    optionEdits.value.forEach((o) => {
      o.to.tool_fallback = fallbackOptionIds.includes(o.id)
      o.type = 'upsert'
    })
  }

  const visibleOptions = computed(() =>
    optionEdits.value
      .filter((o) => o.type === 'upsert')
      .map((o) => ({
        color: o.to.color,
        id: o.id,
        tool_fallback: o.to.tool_fallback,
        value: o.to.value,
      })),
  )

  const getOptionUpdates = (): {
    upsertOptions: Exclude<UpdateSelectPropertyConfig['upsertOptions'], null | undefined>
    removeOptions: UpdateSelectPropertyConfig['removeOptions']
  } => {
    if (!isSelectProperty(editedProperty.value)) {
      return { upsertOptions: [], removeOptions: [] }
    }

    const upsertOptions: UpdateSelectPropertyConfig['upsertOptions'] = []
    const removeOptions: UpdateSelectPropertyConfig['removeOptions'] = []

    optionEdits.value.forEach((edit) => {
      if (edit.type === 'remove' && !edit.from) {
        // remove edit, but the property was never persisted, so noop
        return
      }

      if (edit.type === 'remove' && edit.from) {
        // removal of persisted property
        removeOptions.push(edit.to.value)
        return
      }

      if (edit.type === 'upsert' && edit.from && edit.from.value !== edit.to.value) {
        // rename of persisted property
        upsertOptions.push({
          value: edit.from.value,
          new_value: edit.to.value !== edit.from.value ? edit.to.value : undefined,
          color: edit.to.color,
          tool_fallback: edit.to.tool_fallback,
        })
        return
      }

      if (edit.type === 'upsert' && (!edit.from || !dequal(edit.from, edit.to))) {
        // other update or insert, but not a rename, so no need to set new_value
        upsertOptions.push({
          value: edit.to.value,
          color: edit.to.color,
          tool_fallback: edit.to.tool_fallback,
        })
        return
      }
    })

    return { upsertOptions, removeOptions }
  }

  const updateCollectionProperty = (
    { name: newName, type: newType }: { name: string; type: CollectionSubPropertyType },
    oldName: string,
  ) => {
    const original = originalProperty.value
    const isCollectionType = original?.type === PropertyType.collection
    const pristineExistingCollectionProperties = isCollectionType
      ? (original?.config.properties ?? [])
      : []
    const stagedUpsertCollectionProperties = [...(upsertCollectionProperties.value ?? [])]

    const existingCollectionProperty = pristineExistingCollectionProperties.find(
      (p) => p.name === oldName,
    )

    if (existingCollectionProperty) {
      // existing collection property case
      const stagedIndex = stagedUpsertCollectionProperties.findIndex((p) => p.name === oldName)

      const updateObject = {
        name: existingCollectionProperty.name,
        new_name: newName,
        type: newType,
      }

      if (stagedIndex !== undefined && stagedIndex !== -1) {
        // staged case
        stagedUpsertCollectionProperties.splice(stagedIndex, 1, updateObject)
      } else {
        // stage new updated
        stagedUpsertCollectionProperties.push(updateObject)
      }
    } else {
      // new option case
      const stagedIndex = stagedUpsertCollectionProperties.findIndex((p) => p.name === oldName)

      if (stagedIndex !== undefined && stagedIndex !== -1) {
        // staged case
        const stagedCollectionProperty = nonNullable(stagedUpsertCollectionProperties[stagedIndex])
        stagedCollectionProperty.name = newName
        stagedCollectionProperty.type = newType
      } else {
        // stage new updated
        stagedUpsertCollectionProperties.push({ name: newName, type: newType })
      }
    }

    upsertCollectionProperties.value = stagedUpsertCollectionProperties
  }

  const deleteCollectionProperty = (name: string) => {
    const original = originalProperty.value
    const isCollectionType = original?.type === PropertyType.collection

    const existingCollectionProperty = isCollectionType
      ? original?.config.properties?.find((p) => p.name === name)
      : undefined

    if (existingCollectionProperty) {
      const stagedToRemove = [...(removeCollectionProperties.value ?? [])]
      stagedToRemove.push(name)
      removeCollectionProperties.value = stagedToRemove
    }

    // it might be a newly created property we just need to remove it from the staged upsert state
    const existingUpsertIndex = upsertCollectionProperties.value?.findIndex((p) => p.name === name)
    if (existingUpsertIndex !== undefined && existingUpsertIndex !== -1) {
      upsertCollectionProperties.value?.splice(existingUpsertIndex, 1)
    }
  }

  const createCollectionProperty = (newCollectionProperty: {
    name: string
    type: CollectionSubPropertyType
  }) => {
    const stagedUpsertCollectionProperties = [...(upsertCollectionProperties.value ?? [])]
    // don't allow empty duplicates
    if (stagedUpsertCollectionProperties.some((p) => p.name === newCollectionProperty.name)) return

    // if recreating an option that was deleted, remove it from the delete list
    if (removeCollectionProperties.value?.includes(newCollectionProperty.name)) {
      removeCollectionProperties.value = removeCollectionProperties.value.filter(
        (p) => p !== newCollectionProperty.name,
      )
      return
    }

    stagedUpsertCollectionProperties.push(newCollectionProperty)
    upsertCollectionProperties.value = stagedUpsertCollectionProperties
  }

  const visibleProperties = computed(() => {
    const upsertedMap = Object.fromEntries(
      upsertCollectionProperties.value?.map((p) => [p.name, p]) ?? [],
    )

    const properties =
      editedProperty.value?.type === PropertyType.collection
        ? (editedProperty.value?.config.properties ?? [])
        : []

    return (
      properties
        .filter((property) => !removeCollectionProperties.value?.includes(property.name))
        .map((property) => ({
          // make sure we use the staged value and color if it exists
          name: property.name,
          type: upsertedMap[property.name]?.type ?? property.type,
        }))
        .concat(
          // concat the inserted options at the end
          upsertCollectionProperties.value
            ?.filter((property) => !properties.some((p) => p.name === property.name))
            .map((x) => ({ name: x.name, type: x.type ?? 'text' })) ?? [],
        ) ?? []
    )
  })

  const setName = (name: string) => {
    assertIsNotNullOrUndefined(editedProperty.value)
    editedProperty.value.name = name
  }

  /**
   * Used during prop duplication to change the edited property's type
   */
  const setType = (type: PropertyType) => {
    invariant(editedProperty.value)
    editedProperty.value = convertPropertyType(editedProperty.value, type)
  }

  /**
   * Resolves and mutates the given property's config.properties based on upsert/remove arrays for collection properties.
   * @param property The property to resolve properties for. Must be a collection property.
   */
  const resolveEditedPropertyProperties = (property: Property): void => {
    if (!isCollectionProperty(property)) return

    const upsertPropertiesMap = new Map(
      (upsertCollectionProperties.value ?? []).map((prop) => [
        prop.name,
        {
          name: prop.new_name ?? prop.name,
          type: prop.new_type ?? prop.type ?? 'text',
        },
      ]),
    )
    const processedProperties = (property.config.properties || [])
      .map((prop) => upsertPropertiesMap.get(prop.name) ?? prop)
      .filter((prop) => !(removeCollectionProperties.value ?? []).includes(prop.name))

    const newProperties = Array.from(upsertPropertiesMap.values()).filter((upsertedProperty) => {
      return !processedProperties.some(
        (originalProperty) => originalProperty.name === upsertedProperty.name,
      )
    })

    property.config.properties = [...processedProperties, ...newProperties]
  }

  return {
    editedInputs,
    includesInputId,
    setInput,
    editedProperty,
    setName,
    editedConfig,
    deleteSelectOption,
    visibleOptions,
    isDirty,
    hasUnsavedOptions,
    setType,
    startEditingProperty,
    originalProperty,
    getOptionUpdates,
    optionEdits,
    stopEditingProperty,
    toggleInputId,
    toggleHubInput,
    updateSelectOption,
    createSelectOption,
    toggleSelectOption,
    createCollectionProperty,
    deleteCollectionProperty,
    updateCollectionProperty,
    visibleProperties,
    markOptionAsDefault,
    markOptionsAsFallback,
    upsertCollectionProperties,
    removeCollectionProperties,
    resolveEditedPropertyProperties,
  }
})
